import React from "react";
import {
  ExpandableSection,
  Link,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { AnswersMetadata, Citation } from "features/assistant/definitions";
import { citationLabel } from "./citations";
import styles from "../style.module.scss";

interface CitationsSourcesProps {
  meta: AnswersMetadata;
  onCitationClick: (citation: Citation) => void;
}

const CitationList: React.FC<{
  citations: Citation[];
  onCitationClick: (citation: Citation) => void;
}> = ({ citations, onCitationClick }) => (
  <SpaceBetween size="xxs">
    {citations.map((citation, i) => (
      <Link
        key={citation.citation_id ?? `${citation.title}-${i}`}
        external
        onFollow={(e) => {
          e.preventDefault();
          onCitationClick(citation);
        }}
      >
        {citationLabel(citation)}
      </Link>
    ))}
  </SpaceBetween>
);

/**
 * Sources/References lists shown beneath an Answers message, mirroring the standalone
 * Answers chatbot's expandable "Sources"/"References" sections. Clicking a source opens
 * the cited document. Renders nothing when both lists are empty.
 */
const CitationsSources: React.FC<CitationsSourcesProps> = ({
  meta,
  onCitationClick,
}) => {
  const citations = meta.citations ?? [];
  const references = meta.references ?? [];
  if (citations.length === 0 && references.length === 0) {
    return null;
  }

  return (
    <div className={styles.citationsSources}>
      {citations.length > 0 && (
        <ExpandableSection headerText="Sources" variant="footer">
          <CitationList
            citations={citations}
            onCitationClick={onCitationClick}
          />
        </ExpandableSection>
      )}
      {references.length > 0 && (
        <ExpandableSection headerText="References" variant="footer">
          <CitationList
            citations={references}
            onCitationClick={onCitationClick}
          />
        </ExpandableSection>
      )}
    </div>
  );
};

export default CitationsSources;
